/**
 * Email Provider Detection and Deep Linking Utility
 * 
 * Provides functionality to detect email providers from email addresses
 * and generate deep links to open the user's inbox filtered by verification emails.
 */

const EMAIL_SUBJECT = '9Meals.AI verification code';
const EMAIL_SUBJECT_ENCODED = encodeURIComponent(EMAIL_SUBJECT);

/**
 * Email provider configurations with their domains, display names, icons, and URL generators
 * Note: getUrl and getAppUrl now accept email parameter for account-specific URLs
 */
const EMAIL_PROVIDERS = {
  gmail: {
    name: 'Gmail',
    domains: ['gmail.com', 'googlemail.com'],
    icon: 'gmail',
    // Gmail search for subject - authuser param opens the correct account
    getUrl: (email) => `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(email)}#search/subject%3A${EMAIL_SUBJECT_ENCODED}`,
    // Gmail app deep link - not used for mobile, we use web URL instead for better search support
    getAppUrl: (email) => null,
    color: '#EA4335',
  },
  outlook: {
    name: 'Outlook',
    domains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com', 'passport.com'],
    icon: 'outlook',
    // Outlook web - use the search endpoint with proper query format
    getUrl: (email) => `https://outlook.live.com/mail/0/?login_hint=${encodeURIComponent(email)}&search=subject%3A${EMAIL_SUBJECT_ENCODED}`,
    // Outlook app deep link
    getAppUrl: (email) => `ms-outlook://search?q=${EMAIL_SUBJECT_ENCODED}`,
    color: '#0078D4',
  },
  yahoo: {
    name: 'Yahoo Mail',
    domains: ['yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.ca', 'yahoo.com.au', 'ymail.com', 'rocketmail.com'],
    icon: 'yahoo',
    // Yahoo Mail search
    getUrl: (email) => `https://mail.yahoo.com/d/search/keyword=${EMAIL_SUBJECT_ENCODED}`,
    getAppUrl: (email) => `ymail://search?q=${EMAIL_SUBJECT_ENCODED}`,
    color: '#6001D2',
  },
  icloud: {
    name: 'iCloud Mail',
    domains: ['icloud.com', 'me.com', 'mac.com'],
    icon: 'apple',
    // iCloud Mail doesn't have a search URL, opens inbox
    getUrl: (email) => 'https://www.icloud.com/mail/',
    // Apple Mail app - uses mailto scheme which opens default mail app on Apple devices
    getAppUrl: (email) => null, // Will use native mail app detection
    color: '#3693F3',
  },
  protonmail: {
    name: 'ProtonMail',
    domains: ['protonmail.com', 'protonmail.ch', 'proton.me', 'pm.me'],
    icon: 'protonmail',
    getUrl: (email) => `https://mail.proton.me/u/0/all-mail#keyword=${EMAIL_SUBJECT_ENCODED}`,
    getAppUrl: (email) => null,
    color: '#6D4AFF',
  },
  zoho: {
    name: 'Zoho Mail',
    domains: ['zoho.com', 'zohomail.com'],
    icon: 'zoho',
    getUrl: (email) => `https://mail.zoho.com/zm/#mail/folder/inbox`,
    getAppUrl: (email) => null,
    color: '#C8202B',
  },
  aol: {
    name: 'AOL Mail',
    domains: ['aol.com', 'aim.com'],
    icon: 'aol',
    getUrl: (email) => `https://mail.aol.com/d/search/keyword=${EMAIL_SUBJECT_ENCODED}`,
    getAppUrl: (email) => null,
    color: '#FF0000',
  },
  gmx: {
    name: 'GMX Mail',
    domains: ['gmx.com', 'gmx.net', 'gmx.de', 'gmx.at', 'gmx.ch'],
    icon: 'gmx',
    getUrl: (email) => 'https://www.gmx.com/mail/',
    getAppUrl: (email) => null,
    color: '#1C449B',
  },
  mailcom: {
    name: 'Mail.com',
    domains: ['mail.com'],
    icon: 'mailcom',
    getUrl: (email) => 'https://www.mail.com/mail/',
    getAppUrl: (email) => null,
    color: '#0066CC',
  },
  fastmail: {
    name: 'Fastmail',
    domains: ['fastmail.com', 'fastmail.fm'],
    icon: 'fastmail',
    getUrl: (email) => `https://app.fastmail.com/mail/search:${EMAIL_SUBJECT_ENCODED}`,
    getAppUrl: (email) => null,
    color: '#69639A',
  },
  yandex: {
    name: 'Yandex Mail',
    domains: ['yandex.com', 'yandex.ru', 'ya.ru'],
    icon: 'yandex',
    getUrl: (email) => `https://mail.yandex.com/#search?request=${EMAIL_SUBJECT_ENCODED}`,
    getAppUrl: (email) => null,
    color: '#FF0000',
  },
};

/**
 * Detect the email provider from an email address
 * @param {string} email - The user's email address
 * @returns {object|null} - Provider configuration or null if not recognized
 */
export const detectEmailProvider = (email) => {
  if (!email || !email.includes('@')) return null;
  
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;
  
  for (const [key, provider] of Object.entries(EMAIL_PROVIDERS)) {
    if (provider.domains.some(d => domain === d || domain.endsWith(`.${d}`))) {
      return { id: key, ...provider };
    }
  }
  
  return null;
};

/**
 * Detect if the user is on a mobile device
 * @returns {boolean}
 */
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Detect if the user is on iOS
 * @returns {boolean}
 */
export const isIOS = () => {
  if (typeof window === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

/**
 * Detect if the user is on macOS
 * @returns {boolean}
 */
export const isMacOS = () => {
  if (typeof window === 'undefined') return false;
  return navigator.platform?.toLowerCase().includes('mac');
};

/**
 * Get the appropriate URL to open the user's email
 * Tries app deep link on mobile, falls back to web URL
 * @param {object} provider - The detected email provider
 * @param {string} email - The user's email address
 * @returns {string} - URL to open
 */
export const getEmailUrl = (provider, email) => {
  if (!provider) return null;
  
  // On mobile, try app deep link first for supported providers
  if (isMobile() && provider.getAppUrl) {
    const appUrl = provider.getAppUrl(email);
    if (appUrl) {
      return appUrl;
    }
  }
  
  return provider.getUrl(email);
};

/**
 * Open the user's email client
 * Attempts to open the native app on mobile, falls back to web in new tab
 * @param {string} email - The user's email address
 * @returns {boolean} - Whether a provider was detected and opened
 */
export const openEmailClient = (email) => {
  const provider = detectEmailProvider(email);
  if (!provider) return false;
  
  // For Gmail on mobile, try to open the app, fallback to web in new tab
  if (provider.id === 'gmail' && isMobile()) {
    const webUrl = provider.getUrl(email);
    
    // Try Gmail app deep link first, fall back to web URL in new tab
    // Using googlegmail scheme to open app directly (if installed)
    const gmailAppUrl = 'googlegmail:///';
    tryOpenAppWithFallback(gmailAppUrl, webUrl);
    return true;
  }
  
  // For Outlook on mobile
  if (provider.id === 'outlook' && isMobile()) {
    const appUrl = provider.getAppUrl(email);
    const webUrl = provider.getUrl(email);
    
    if (appUrl) {
      // Try app URL, fall back to web in new tab
      tryOpenAppWithFallback(appUrl, webUrl);
      return true;
    }
  }
  
  // For Yahoo on mobile  
  if (provider.id === 'yahoo' && isMobile()) {
    const appUrl = provider.getAppUrl(email);
    const webUrl = provider.getUrl(email);
    
    if (appUrl) {
      tryOpenAppWithFallback(appUrl, webUrl);
      return true;
    }
  }
  
  // For iCloud on Apple devices, try to open Apple Mail app
  if (provider.id === 'icloud' && (isIOS() || isMacOS())) {
    // Apple Mail doesn't have a great search URL, just open inbox
    window.open(provider.getUrl(email), '_blank');
    return true;
  }
  
  // Default: Open web URL in new tab
  const url = provider.getUrl(email);
  if (!url) return false;
  
  window.open(url, '_blank');
  return true;
};

/**
 * Try to open an app URL, fall back to web URL in new tab if app isn't installed
 * Uses visibility change detection for more reliable fallback
 */
const tryOpenAppWithFallback = (appUrl, webUrl) => {
  let appOpened = false;
  
  const handleVisibilityChange = () => {
    if (document.hidden) {
      appOpened = true;
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Try to open the app
  window.location.href = appUrl;
  
  // If the page is still visible after a delay, app didn't open - open web in new tab
  setTimeout(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    if (!appOpened) {
      window.open(webUrl, '_blank');
    }
  }, 1500);
};

/**
 * Get provider icon component
 * Returns an SVG icon for the detected provider
 */
export const getProviderIcon = (providerId) => {
  const icons = {
    gmail: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#EA4335"/>
      </svg>
    ),
    outlook: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#0078D4"/>
      </svg>
    ),
    yahoo: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#6001D2"/>
      </svg>
    ),
    apple: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#3693F3"/>
      </svg>
    ),
    protonmail: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#6D4AFF"/>
      </svg>
    ),
    default: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#65635D"/>
      </svg>
    ),
  };
  
  return icons[providerId] || icons.default;
};

const emailProviders = {
  detectEmailProvider,
  openEmailClient,
  getEmailUrl,
  getProviderIcon,
  isMobile,
  isIOS,
  isMacOS,
};

export default emailProviders;
